import React, { useEffect, useRef, useState } from "react";
import { Modal, Button, Input, Space, Alert } from "antd";
import { notify } from "../../utils/notify";

// D5-T1 — scan a QR with the camera (native BarcodeDetector) and return its raw
// value; falls back to manual code entry where BarcodeDetector/camera isn't available.
export default function QrScanner({ open, onClose, onResult, title = "Scan QR code" }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const [supported, setSupported] = useState(true);
  const [manual, setManual] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    const hasDetector = "BarcodeDetector" in window;
    setSupported(hasDetector);

    const stop = () => {
      active = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
    const handle = (val) => {
      stop();
      onResult(val);
    };

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" }, audio: false });
        if (!active) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
        if (hasDetector) {
          const detector = new window.BarcodeDetector({ formats: ["qr_code"] });
          const tick = async () => {
            if (!active || !videoRef.current) return;
            try {
              const codes = await detector.detect(videoRef.current);
              if (codes && codes.length) return handle(codes[0].rawValue);
            } catch (e) {
              /* transient detect error */
            }
            rafRef.current = requestAnimationFrame(tick);
          };
          rafRef.current = requestAnimationFrame(tick);
        }
      } catch (e) {
        setSupported(false);
        notify.warning("Camera unavailable — enter the code manually.");
      }
    })();

    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submitManual = () => {
    if (manual.trim()) {
      onResult(manual.trim());
      setManual("");
    }
  };

  return (
    <Modal open={open} onCancel={onClose} footer={null} title={title} destroyOnClose>
      <video ref={videoRef} playsInline muted style={{ width: "100%", borderRadius: 8, background: "#000" }} />
      {!supported && (
        <Alert style={{ marginTop: 12 }} type="info" showIcon message="Live QR scanning isn't available here — enter the code below." />
      )}
      <Space.Compact style={{ width: "100%", marginTop: 12 }}>
        <Input placeholder="Or type the outlet code" value={manual} onChange={(e) => setManual(e.target.value)} onPressEnter={submitManual} />
        <Button type="primary" onClick={submitManual}>Use code</Button>
      </Space.Compact>
    </Modal>
  );
}

// The F2 outlet QR encodes JSON {t:"shop", code, id}; tolerate a raw code too.
export function parseShopCode(raw) {
  try {
    const o = JSON.parse(raw);
    return o.code || o.id || raw;
  } catch (e) {
    return raw;
  }
}
